import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Paper,
  Breadcrumbs,
  Link as MuiLink,
  TablePagination,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ReplayIcon from '@mui/icons-material/Replay';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import getAllSystemProviders from '../../../Apollo/queries/getAllSystemProviders';
import deleteSystemProviderMutation from '../../../Apollo/mutations/deleteSystemProvider';
import setSystemProviderActiveMutation from '../../../Apollo/mutations/setSystemProviderActive';
import { PagePathname } from '../../../routes/paths';

interface SystemProvider {
  id: number;
  name: string;
  recipient_email: string;
  is_active: number;
  letter_template_id: number | null;
  letter_template_name: string | null;
  createdAt: string;
  updatedAt: string;
}

export function countActive(providers: Pick<SystemProvider, 'is_active'>[]): number {
  return providers.filter((p) => p.is_active !== 0).length;
}

export function countInactive(providers: Pick<SystemProvider, 'is_active'>[]): number {
  return providers.filter((p) => p.is_active === 0).length;
}

function SystemProviderListPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<SystemProvider | null>(null);
  const [inUseDialogOpen, setInUseDialogOpen] = useState(false);
  const [inUseMessage, setInUseMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(getAllSystemProviders, {
    variables: { search: searchTerm || undefined },
    fetchPolicy: 'network-only',
  });

  const [deleteProvider] = useMutation(deleteSystemProviderMutation, {
    onCompleted: () => {
      refetch();
      setDeleteDialogOpen(false);
      setProviderToDelete(null);
    },
    onError: (err) => {
      const code = (err.graphQLErrors?.[0]?.extensions?.code as string) ?? '';
      if (code === 'PROVIDER_IN_USE') {
        const ext = err.graphQLErrors[0].extensions as { scheduledCount?: number; advisorCount?: number };
        const scheduledCount = ext.scheduledCount ?? 0;
        const msg =
          scheduledCount > 0
            ? 'Dieser Dienstleister kann nicht gelöscht werden, da noch ausstehende Versandvorgänge vorhanden sind.'
            : 'Dieser Dienstleister kann nicht gelöscht werden, da er noch aktiv genutzt wird.';
        setDeleteDialogOpen(false);
        setProviderToDelete(null);
        setInUseMessage(msg);
        setInUseDialogOpen(true);
      }
    },
  });

  const [setProviderActive] = useMutation(setSystemProviderActiveMutation, {
    onCompleted: () => refetch(),
    onError: (err) => {
      const code = (err.graphQLErrors?.[0]?.extensions?.code as string) ?? '';
      if (code === 'NO_TEMPLATE') {
        setToggleError('Reaktivieren nicht möglich: keine Briefvorlage zugeordnet.');
      } else if (code === 'NOT_FOUND') {
        setToggleError('Dienstleister nicht gefunden.');
      } else {
        setToggleError('Statusänderung fehlgeschlagen. Bitte Seite neu laden.');
      }
    },
  });

  const providers: SystemProvider[] = data?.getAllSystemProviders || [];
  const activeCount = countActive(providers);
  const inactiveCount = countInactive(providers);

  useEffect(() => {
    setPage(0);
  }, [providers.length, searchTerm]);

  const pagedProviders =
    rowsPerPage === -1 ? providers : providers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDelete = () => {
    if (providerToDelete) {
      deleteProvider({ variables: { id: providerToDelete.id } });
    }
  };

  const openDeleteDialog = (provider: SystemProvider) => {
    setProviderToDelete(provider);
    setDeleteDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to={PagePathname.einstellung} underline="hover" color="inherit">
          Einstellungen
        </MuiLink>
        <Typography color="text.primary">Systemdienstleister</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Systemdienstleister
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Externe Systeme verwalten – Zugänge werden über diese Dienstleister beantragt
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(PagePathname.systemdienstleisterNeu)}
          sx={{
            bgcolor: '#4ADE80',
            color: '#000',
            fontWeight: 'bold',
            '&:hover': { bgcolor: '#22C55E' },
          }}
        >
          Neuer Dienstleister
        </Button>
      </Box>

      {/* Statistics */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper
          sx={{
            flex: 1,
            p: 2,
            bgcolor: 'rgba(74, 222, 128, 0.1)',
            border: '1px solid rgba(74, 222, 128, 0.3)',
          }}
        >
          <Typography variant="h4" fontWeight="bold" color="#4ADE80">
            {providers.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dienstleister gesamt
          </Typography>
        </Paper>
        <Paper
          sx={{
            flex: 1,
            p: 2,
            bgcolor: 'rgba(74, 222, 128, 0.05)',
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            {activeCount}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aktiv
          </Typography>
        </Paper>
        <Paper
          sx={{
            flex: 1,
            p: 2,
            bgcolor: 'rgba(107, 114, 128, 0.05)',
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            {inactiveCount}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Inaktiv
          </Typography>
        </Paper>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Dienstleister suchen..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Table or Empty State */}
      {!loading && providers.length === 0 && !searchTerm ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LinkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Noch keine Dienstleister angelegt
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(PagePathname.systemdienstleisterNeu)}
            sx={{
              mt: 2,
              bgcolor: '#4ADE80',
              color: '#000',
              fontWeight: 'bold',
              '&:hover': { bgcolor: '#22C55E' },
            }}
          >
            Ersten Dienstleister anlegen
          </Button>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    E-Mail (Empfänger)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Briefvorlage
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }} align="right">
                    Aktionen
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedProviders.map((provider) => (
                  <TableRow key={provider.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon fontSize="small" color="action" />
                        <Typography fontWeight="bold">{provider.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography color="text.secondary">{provider.recipient_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="text.secondary">{provider.letter_template_name || '\u2014'}</Typography>
                    </TableCell>
                    <TableCell>
                      {provider.is_active ? (
                        <Chip
                          label="Aktiv"
                          size="small"
                          icon={
                            <Box
                              component="span"
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#22C55E',
                                ml: '8px !important',
                              }}
                            />
                          }
                          sx={{
                            bgcolor: '#16A34A22',
                            color: '#22C55E',
                            fontWeight: 'bold',
                            '& .MuiChip-icon': { color: '#22C55E' },
                          }}
                        />
                      ) : (
                        <Chip
                          label="Inaktiv"
                          size="small"
                          icon={
                            <Box
                              component="span"
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#9CA3AF',
                                ml: '8px !important',
                              }}
                            />
                          }
                          sx={{
                            bgcolor: '#6B728022',
                            color: '#9CA3AF',
                            fontWeight: 'bold',
                            '& .MuiChip-icon': { color: '#9CA3AF' },
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/einstellung/systemdienstleister/${provider.id}`)}
                        sx={{ mr: 1 }}
                      >
                        Bearbeiten
                      </Button>
                      {provider.is_active ? (
                        <Tooltip title="Dienstleister deaktivieren">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => setProviderActive({ variables: { id: provider.id, active: false } })}
                            sx={{ mr: 0.5 }}
                          >
                            <PowerSettingsNewIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            provider.letter_template_id ? 'Dienstleister reaktivieren' : 'erst Briefvorlage zuordnen'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={!provider.letter_template_id}
                              onClick={() => setProviderActive({ variables: { id: provider.id, active: true } })}
                              sx={{ mr: 0.5 }}
                            >
                              <ReplayIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <IconButton size="small" color="error" onClick={() => openDeleteDialog(provider)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={providers.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, { label: 'Alle', value: -1 }]}
            labelRowsPerPage="Zeilen pro Seite"
            labelDisplayedRows={({ from, to, count }) => `${from}\u2013${to} von ${count}`}
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {providers.length} Dienstleister gesamt
          </Typography>
        </>
      )}

      {/* Toggle-Fehler-Snackbar */}
      <Snackbar
        open={!!toggleError}
        autoHideDuration={6000}
        onClose={() => setToggleError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setToggleError(null)}>
          {toggleError}
        </Alert>
      </Snackbar>

      {/* In-Use Info Dialog */}
      <Dialog open={inUseDialogOpen} onClose={() => setInUseDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="info" />
          Löschen nicht möglich
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{inUseMessage}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={() => setInUseDialogOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <DeleteIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
          <br />
          Dienstleister löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du den Dienstleister <strong>{providerToDelete?.name}</strong> wirklich löschen? Diese Aktion kann
            nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Abbrechen
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SystemProviderListPage;
